/* Lepinceau rebuild — motion layer (styles in assets/lp-motion.css).
   Hooks into the rebuilt sections' existing classes; no section markup depends on it.

   Safety rules:
   - Nothing is hidden until this script tags it, so without JS every section simply shows.
   - Anything already on screen when the script runs is left alone (no flash).
   - Off in the theme editor and for visitors who ask for reduced motion.
   - transform / opacity / clip-path only; parallax is rAF-throttled and only runs
     while its element is on screen. */
(function () {
  'use strict';

  if (!('IntersectionObserver' in window)) return;
  if (window.Shopify && window.Shopify.designMode) return;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduce.matches) return;

  var root = document.documentElement;
  root.classList.add('lp-motion');

  var STAGGER_MS = 90;
  var STAGGER_MAX = 6;

  /* [selector, effect]. Effects: rise (fade + rise), title (rise + blur-in),
     curtain (clip-path open + image settle), wordmark (slow rise from the page edge),
     draw (line icons draw in). */
  var SINGLES = [
    ['.lp-products__head, .lp-cats__heading, .lp-cats__sub, .lp-journal__head, .lp-faq__intro, .lp-voices__intro, .lp-gallery__head', 'title'],
    ['.lp-about__heading', 'title'],
    ['.lp-about__lead, .lp-about__text, .lp-about__copy .lp-btn, .lp-products__foot', 'rise'],
    ['.lp-about__media, .lp-contact__visual', 'curtain'],
    ['.lp-voices__stage', 'rise'],
    ['.lp-article__cta, .lp-article__more-heading', 'rise'],
    ['.lp-contact__heading', 'title'],
    ['.lp-footer__news', 'rise'],
    ['.lp-footer__wordmark', 'wordmark']
  ];

  /* [container, children]: children cascade in once the container is in view. */
  var GROUPS = [
    ['.lp-trust__list', '.lp-trust__item'],
    ['.lp-products__track', '.lp-products__item'],
    ['.lp-cats__grid', '.lp-cats__item'],
    ['.lp-about__features', '.lp-about__feature'],
    ['.lp-journal__list', '.lp-journal__item'],
    ['.lp-faq__list', '.lp-faq__item'],
    ['.lp-blogpage__grid', '.lp-blogpage__item'],
    ['.lp-article__more-list', 'li'],
    ['.lp-contact__info', '.lp-contact__info-item'],
    ['.lp-footer__cols', '.lp-footer__col']
  ];

  /* Line icons that draw themselves when their group arrives. */
  var DRAW_ICONS = '.lp-trust__icon svg, .lp-about__icon svg';

  /* Parallax: [selector, strength]. Strength = share of the element's height it drifts. */
  var PARALLAX = [
    ['.lp-hero__media', 0.04],
    ['.lp-about__media img', 0.05]
  ];

  function inViewport(el) {
    var r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight && r.width > 0;
  }

  var revealer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var target = entry.target;
      target.classList.add('is-in');
      if (target.hasAttribute('data-lp-group')) {
        target.querySelectorAll('[data-lp-reveal]').forEach(function (child) {
          child.classList.add('is-in');
        });
      }
      revealer.unobserve(target);
    });
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 });

  function tag(el, effect) {
    // Skip elements not rendered at this width (e.g. a shelf's mobile-only foot).
    if (el.hasAttribute('data-lp-reveal') || el.getClientRects().length === 0 || inViewport(el)) return false;
    el.setAttribute('data-lp-reveal', effect);
    return true;
  }

  // Elements at the very bottom of the page (the footer wordmark) can never cross the
  // revealer's -12% line when the page ends — they trigger at the screen edge instead.
  var edgeRevealer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in');
      edgeRevealer.unobserve(entry.target);
    });
  }, { threshold: 0.01 });

  SINGLES.forEach(function (pair) {
    document.querySelectorAll(pair[0]).forEach(function (el) {
      if (!tag(el, pair[1])) return;
      (pair[1] === 'wordmark' ? edgeRevealer : revealer).observe(el);
    });
  });

  GROUPS.forEach(function (pair) {
    document.querySelectorAll(pair[0]).forEach(function (group) {
      var items = group.querySelectorAll(pair[1]);
      if (!items.length) return;
      // A tall group that starts on screen (e.g. the blog grid): leave the visible
      // items alone and reveal the rest one by one as they scroll in.
      if (inViewport(group)) {
        items.forEach(function (item) {
          if (tag(item, 'rise')) revealer.observe(item);
        });
        return;
      }
      group.setAttribute('data-lp-group', '');
      items.forEach(function (item, i) {
        item.setAttribute('data-lp-reveal', item.matches('.lp-cats__item') ? 'curtain' : 'rise');
        item.style.setProperty('--lp-delay', Math.min(i, STAGGER_MAX) * STAGGER_MS + 'ms');
      });
      revealer.observe(group);
    });
  });

  // Icons inside cascading groups draw in with their card.
  document.querySelectorAll('[data-lp-group]').forEach(function (group) {
    group.querySelectorAll(DRAW_ICONS).forEach(function (svg) {
      svg.setAttribute('data-lp-draw', '');
      svg.querySelectorAll('path, rect, circle, line, polyline').forEach(function (shape) {
        shape.setAttribute('pathLength', '1');
      });
    });
  });

  /* ---- Hero: headline words rise in sequence (text stays one h1, read as one). ---- */
  document.querySelectorAll('.lp-hero__heading').forEach(function (heading) {
    var text = heading.textContent.trim();
    if (!text || heading.children.length) return;
    heading.setAttribute('aria-label', text);
    heading.textContent = '';
    text.split(/\s+/).forEach(function (word, i, words) {
      var outer = document.createElement('span');
      var inner = document.createElement('span');
      outer.className = 'lp-word';
      outer.setAttribute('aria-hidden', 'true');
      inner.textContent = word;
      inner.style.setProperty('--lp-delay', 150 + i * 110 + 'ms');
      outer.appendChild(inner);
      heading.appendChild(outer);
      if (i < words.length - 1) heading.appendChild(document.createTextNode(' '));
    });
    heading.classList.add('lp-split');
  });

  /* ---- Parallax ---- */
  var active = new Set();
  var ticking = false;

  var watcher = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        active.add(entry.target);
      } else {
        active.delete(entry.target);
      }
    });
    requestTick();
  }, { rootMargin: '10% 0px' });

  PARALLAX.forEach(function (pair) {
    document.querySelectorAll(pair[0]).forEach(function (el) {
      el.setAttribute('data-lp-parallax', '');
      el.lpStrength = pair[1];
      watcher.observe(el);
    });
  });

  function update() {
    ticking = false;
    var vh = window.innerHeight;
    active.forEach(function (el) {
      var box = (el.parentElement || el).getBoundingClientRect();
      // -1 when the frame's centre is at the bottom of the screen, +1 at the top.
      var progress = (vh / 2 - (box.top + box.height / 2)) / (vh / 2 + box.height / 2);
      progress = Math.max(-1, Math.min(1, progress));
      el.style.setProperty('--lp-py', (progress * el.lpStrength * box.height).toFixed(1) + 'px');
    });
  }

  function requestTick() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  window.addEventListener('scroll', requestTick, { passive: true });
  window.addEventListener('resize', requestTick);

  // A visitor who switches reduced motion on mid-visit: show everything, stop moving.
  var stop = function () {
    if (!reduce.matches) return;
    root.classList.remove('lp-motion');
    window.removeEventListener('scroll', requestTick);
    document.querySelectorAll('[data-lp-parallax]').forEach(function (el) {
      el.style.removeProperty('--lp-py');
    });
  };
  if (reduce.addEventListener) {
    reduce.addEventListener('change', stop);
  } else if (reduce.addListener) {
    reduce.addListener(stop);
  }
})();

/* Wishlist heart feedback on product cards. Separate from the reveal layer above so
   it also runs where that one bails out; reduced motion is handled in the CSS.
   Wokiee's own click handler (bound on body) runs first and flips data-action,
   so by the time this listener on document fires, the new state is readable. */
(function () {
  'use strict';

  function replay(el, cls) {
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.lp-card__actions .wlbutton-js');
    if (!btn) return;

    window.requestAnimationFrame(function () {
      var saved = btn.getAttribute('data-action') === 'wk-remove';
      btn.classList.remove('is-popping', 'is-unpopping');
      replay(btn, saved ? 'is-popping' : 'is-unpopping');

      var burst = btn.parentNode.querySelector('.lp-card__burst');
      if (burst && saved) replay(burst, 'is-bursting');
    });
  });

  document.addEventListener('animationend', function (e) {
    var el = e.target;
    if (!el.classList) return;
    if (el.classList.contains('lp-card__burst')) {
      if (e.pseudoElement === '::after') el.classList.remove('is-bursting');
    } else if (el.classList.contains('wlbutton-js')) {
      el.classList.remove('is-popping', 'is-unpopping');
    }
  });
})();
